import * as React from 'react';

import { DETAIL_LAYOUT_PANE_SIZES } from './detail-layout-pane-sizes.js';
import { rootFontSize } from './root-font-size.js';

type UseDetailLayoutFoldsOptions = Readonly<{
  isMobile: boolean;
  width: number | null;
  /** Whether the page wants its right pane beside the record, before checking there is room for it. */
  rightPaneWanted: boolean;
  onLeftPaneFold: () => void;
}>;

export function useDetailLayoutFolds({
  isMobile,
  width,
  rightPaneWanted,
  onLeftPaneFold,
}: UseDetailLayoutFoldsOptions) {
  // Fold points are in rem so they scale with browser text size exactly as the panel minimums do.
  const [remPx] = React.useState(rootFontSize);
  // A null width (first paint) counts as wide so a desktop right pane doesn't flash into a tab and back.
  const roomForRightPane =
    !isMobile && (width === null || width >= DETAIL_LAYOUT_PANE_SIZES.right.foldRem * remPx);
  const rightPaneVisible = rightPaneWanted && roomForRightPane;
  const leftFoldRem = rightPaneVisible
    ? DETAIL_LAYOUT_PANE_SIZES.left.foldBesideRightPaneRem
    : DETAIL_LAYOUT_PANE_SIZES.left.foldRem;
  const leftPaneAsSheet = isMobile || (width !== null && width < leftFoldRem * remPx);

  const wasSheet = React.useRef(leftPaneAsSheet);
  React.useEffect(() => {
    if (leftPaneAsSheet && !wasSheet.current) onLeftPaneFold();
    wasSheet.current = leftPaneAsSheet;
  }, [leftPaneAsSheet, onLeftPaneFold]);

  return {
    roomForRightPane,
    rightPaneVisible,
    leftPaneAsSheet,
    measured: isMobile || width !== null,
  };
}
